export type NotificationChannel = 'email' | 'slack';

export interface ChannelPreference {
  enabled: boolean;
  parameters: string[];
}

export interface NotificationPreferencesDraft {
  email: ChannelPreference;
  slack: ChannelPreference;
}

export interface StoredNotificationPreferences {
  email?: Partial<ChannelPreference>;
  slack?: Partial<ChannelPreference>;
}

export interface PreferenceDefinition {
  id: string;
  title: string;
  description: string;
  parameter: string;
}

export interface PreferenceSection {
  id: string;
  title: string;
  description: string;
  preferences: PreferenceDefinition[];
}

export const CHECKIN_REMINDER_PARAMETER = 'Check-in reminder';
export const CHECKIN_OVERDUE_REMINDER_PARAMETER = 'Check-in overdue reminder';

interface PreferenceCatalogueOptions {
  isAdmin: boolean;
  isCheckinsEnabled: boolean;
  isGoalsEnabled: boolean;
  checkinSubmissionParameter: string;
}

const normalizeParameters = (parameters?: string[]) =>
  Array.from(new Set(parameters || [])).sort();

export const createNotificationPreferencesDraft = (
  notifications?: StoredNotificationPreferences,
): NotificationPreferencesDraft => ({
  email: {
    enabled: notifications?.email?.enabled ?? true,
    parameters: normalizeParameters(notifications?.email?.parameters),
  },
  slack: {
    enabled: notifications?.slack?.enabled ?? false,
    parameters: normalizeParameters(notifications?.slack?.parameters),
  },
});

export const toggleNotificationParameter = (
  parameters: string[],
  parameter: string,
) =>
  parameters.includes(parameter)
    ? parameters.filter((item) => item !== parameter)
    : normalizeParameters([...parameters, parameter]);

export const notificationPreferencesAreEqual = (
  left: NotificationPreferencesDraft,
  right: NotificationPreferencesDraft,
) =>
  left.email.enabled === right.email.enabled &&
  left.slack.enabled === right.slack.enabled &&
  normalizeParameters(left.email.parameters).join('|') ===
    normalizeParameters(right.email.parameters).join('|') &&
  normalizeParameters(left.slack.parameters).join('|') ===
    normalizeParameters(right.slack.parameters).join('|');

export const buildEmailPreferenceSections = ({
  isAdmin,
  isCheckinsEnabled,
  isGoalsEnabled,
  checkinSubmissionParameter,
}: PreferenceCatalogueOptions): PreferenceSection[] => {
  const sections: PreferenceSection[] = [];

  if (isCheckinsEnabled) {
    const checkinPreferences: PreferenceDefinition[] = [
      {
        id: 'checkinReminder',
        title: 'Check-in reminder',
        description: 'A reminder when your check-in is due.',
        parameter: CHECKIN_REMINDER_PARAMETER,
      },
      {
        id: 'checkinOverdueReminder',
        title: 'Check-in overdue reminder',
        description: 'A reminder when your check-in is overdue.',
        parameter: CHECKIN_OVERDUE_REMINDER_PARAMETER,
      },
    ];

    if (isAdmin) {
      checkinPreferences.push({
        id: 'checkinSubmitted',
        title: 'Check-in submissions',
        description:
          'When someone submits a check-in in a cycle you can manage.',
        parameter: checkinSubmissionParameter,
      });
    }

    sections.push({
      id: 'checkins',
      title: 'Check-ins',
      description: 'Reminders and submission activity for your check-ins.',
      preferences: checkinPreferences,
    });
  }

  if (isGoalsEnabled) {
    sections.push({
      id: 'goals',
      title: 'Goals & KPIs',
      description: 'Changes to goals and KPIs you own or follow.',
      preferences: [
        {
          id: 'goalUpdate',
          title: 'Goal and KPI updates',
          description: 'Progress and status updates on goals and KPIs.',
          parameter: 'OKR & KPIs updates',
        },
        {
          id: 'goalEdit',
          title: 'Goal and KPI edits',
          description: 'Edits to goal details, owners, dates, or targets.',
          parameter: 'OKR & KPIs edit',
        },
      ],
    });
  }

  return sections;
};
